import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import {
  AttendeeInformation,
  CalendarEvent,
  DATETIMEFORMART,
  EventRequest,
  MeetingStepperDetails,
  MeetingStepperErrorCodes,
  Place,
  reminderMinutes,
  WorkingHours,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { PlacesService } from 'src/app/services/placesService/places.service';
import { WebSocketService } from 'src/app/services/web-socket.service';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { UserStore } from 'src/app/stores/user.store';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import { PlacesStore } from 'src/app/stores/places.store';
import { AddAttendeeComponent } from '../add-attendee/add-attendee.component';
@Component({
  selector: 'app-booking-stepper',
  templateUrl: './booking-stepper.component.html',
  styleUrls: ['./booking-stepper.component.scss'],
})
export class BookingStepperComponent implements OnInit, OnDestroy {
  // steps are : 1. select date and time, 2. select category, 3. select participants, 4. select isOnlineMeeting, 5. select Subjet , 6. select reminder, 7. summary , 8. booking result
  @Input() initialStartTime?: string;
  @Input() initialEndTime?: string;

  currentStep = 1;
  totalSteps = 7;
  locale = this.datetimeFormatService.selectedDateTimeFormat;
  useWorkingHours = false;
  workingHours: WorkingHours;
  invitableAttendees: AttendeeInformation[] = [];
  filteredAttendees: AttendeeInformation[] = [];
  localSubjectString = '';
  showTryAgainButton = false;
  attendeeAvailabilityLoading = false;
  attendeeSearchQuery: string = '';
  localSelectedReminder = null;
  bookingResultSubscription: Subscription;
  infoMessage = '';
  calendarEvent: CalendarEvent;
  alternativeResource: Place = null;
  showAs: number;
  reminderOptions = [
    { text: '15 minutes', value: 15 },
    { text: '30 minutes', value: 30 },
    { text: '1 hour', value: 60 },
    { text: '2 hours', value: 120 },
    { text: '1 day', value: 1440 },
    { text: '2 days', value: 2880 },
  ];
  resourcesCategories = [
    {
      name: 'Room',
      icon: 'business-outline',
      disabled: !this.checkIfCategoryExists('Room'),
    },
    {
      name: 'Workspace',
      icon: 'briefcase-outline',
      disabled: !this.checkIfCategoryExists('Workspace'),
    },
    {
      name: 'Workplace',
      icon: 'laptop-outline',
      disabled: !this.checkIfCategoryExists('Workplace'),
    },
    {
      name: 'Parking',
      icon: 'car-outline',
      disabled: !this.checkIfCategoryExists('Parking'),
    },
  ];
  meetingDetails: MeetingStepperDetails = {
    startTime: moment()
      .add(5 - (moment().minute() % 5), 'minutes')
      .format(DATETIMEFORMART),
    endTime: moment()
      .add(5 - (moment().minute() % 5) + 30, 'minutes')
      .format(DATETIMEFORMART),
    category: '',
    capacity: null,
    participants: [],
    isOnlineMeeting: false,
    subject: '',
    reminder: null,
    prioritizeFavoritePlaces: false,
    acceptGreaterCapacity: false,
    acceptSmallerCapacity: false,
  };
  constructor(
    private modalController: ModalController,
    private webSocketService: WebSocketService,
    private datetimeFormatService: DatetimeFormatService,
    private attendeesStore: AttendeesStore,
    private apiService: ApiService,
    private translateService: TranslateService,
    private placesService: PlacesService,
    private router: Router,
    private bookingsStore: BookingsStore,
    private userStore: UserStore,
    private placesStore: PlacesStore,
  ) {}

  ngOnInit() {
    const userInformation = this.userStore.user();
    this.workingHours = userInformation?.workingHours;

    const userPreferences = this.userStore.userPreferences();
    this.showAs = userPreferences?.userSettings?.bookingStatus;

    if (this.initialStartTime && this.initialEndTime) {
      this.meetingDetails.startTime = this.initialStartTime;
      this.meetingDetails.endTime = this.initialEndTime;
    }
  }

  ngOnDestroy() {
    if (this.bookingResultSubscription)
      this.bookingResultSubscription.unsubscribe();
  }

  dismissModal(isProcessFinished: boolean = false) {
    this.modalController.dismiss();
    if (isProcessFinished) {
      this.router.navigate(['/tabs/bookings']);
    }
  }

  handleNextStep(isSkipped: boolean = false) {
    switch (this.currentStep) {
      case 1: // select date and time
      case 3: // select capacity
      case 4: // select isOnlineMeeting
        this.currentStep++;
        break;
      case 2: // select category
        this.handleCategorySelection();
        break;
      case 5: // select subject
        this.meetingDetails.subject = isSkipped
          ? 'HXA Scheduled Booking'
          : this.localSubjectString;
        this.currentStep++;
        break;
      case 6: // select reminder
        if (!isSkipped)
          this.meetingDetails.reminder = this.localSelectedReminder;
        this.currentStep++;
        break;
      case 7: // summary
        this.startBookingProcess();
        this.currentStep++;
        break;
      case 8: // booking result
        isSkipped ? this.bookAlternativeResource() : this.dismissModal(true);
        break;
    }
  }

  private handleCategorySelection() {
    switch (this.meetingDetails.category) {
      case 'Room':
        this.invitableAttendees = JSON.parse(
          JSON.stringify(this.attendeesStore.attendees()),
        );
        this.filteredAttendees = [...this.invitableAttendees];
        this.currentStep++;
        break;
      case 'Parking':
        this.generateSubjectForParking();
        this.meetingDetails.isOnlineMeeting = false;
        this.meetingDetails.capacity = 1;
        this.meetingDetails.participants = [];
        this.currentStep += 3; // for parking we skip capacity and isOnlineMeeting
        break;
      default: // for workspace and workplaces
        this.meetingDetails.capacity = 1;
        this.meetingDetails.participants = [];
        this.currentStep += 2;
        break;
    }
  }

  private generateSubjectForParking() {
    const userInformation = this.userStore.user();
    this.localSubjectString =
      this.translateService.instant('vehicleNumberSubject') +
      ' ' +
      userInformation?.vehicleNumber;
  }

  get disableNextButton() {
    return (
      (this.currentStep == 2 && this.meetingDetails.category == '') ||
      (this.currentStep == 3 && this.meetingDetails.capacity == null) ||
      (this.currentStep == 5 && this.localSubjectString == '') ||
      (this.currentStep == 6 && this.localSelectedReminder == null)
    );
  }

  get showSkipButton() {
    return this.currentStep == 5 || this.currentStep == 6;
  }

  get buttonText() {
    if (this.currentStep < this.totalSteps)
      return this.translateService.instant('onboarding.next');
    if (this.currentStep == this.totalSteps)
      return this.translateService.instant('book');
    if (this.calendarEvent)
      return this.translateService.instant('onboarding.done');
    return '';
  }

  isAttendeeSelected(attendee: AttendeeInformation) {
    return this.meetingDetails.participants.find(
      (p) => p.userPrincipalName == attendee.userPrincipalName,
    );
  }

  handlePreviousStep() {
    if (this.currentStep == 4 && this.meetingDetails.category != 'Room')
      return (this.currentStep = 2);
    if (this.currentStep == 5 && this.meetingDetails.category == 'Parking')
      return (this.currentStep = 2);
    if (this.currentStep == 8) {
      this.resetComponentData();
      return (this.currentStep = 1);
    }
    this.currentStep--;
  }

  private resetComponentData() {
    this.alternativeResource = null;
    this.calendarEvent = null;
    this.attendeeSearchQuery = '';
    this.localSelectedReminder = null;
    this.localSubjectString = '';
    this.infoMessage = '';
    this.showTryAgainButton = false;
    this.useWorkingHours = false;
    this.bookingResultSubscription?.unsubscribe();
    this.meetingDetails = {
      startTime: moment()
        .add(5 - (moment().minute() % 5), 'minutes')
        .format(DATETIMEFORMART),
      endTime: moment()
        .add(5 - (moment().minute() % 5) + 30, 'minutes')
        .format(DATETIMEFORMART),
      category: '',
      capacity: null,
      participants: [],
      isOnlineMeeting: false,
      subject: '',
      reminder: null,
      prioritizeFavoritePlaces: false,
      acceptGreaterCapacity: false,
      acceptSmallerCapacity: false,
    };
  }

  handleTimeChange(event: any, type: string) {
    this.meetingDetails[type] = event.detail.value;
    if (type == 'startTime') {
      if (this.useWorkingHours) {
        this.meetingDetails.endTime = moment(this.meetingDetails.startTime)
          .set('hours', this.workingHours.endTime.hour)
          .set('minutes', this.workingHours.endTime.minute)
          .format(DATETIMEFORMART);
      } else {
        this.meetingDetails.endTime = moment(this.meetingDetails.startTime)
          .add(1, 'hour')
          .format(DATETIMEFORMART);
      }
    }
  }

  handleCategoryClick(category: any) {
    this.meetingDetails.category = category.name;
  }

  handleOnlineMeetingClick(isOnlineMeeting: boolean) {
    this.meetingDetails.isOnlineMeeting = isOnlineMeeting;
  }

  handleReminderClick(reminder: any) {
    this.localSelectedReminder = reminder;
  }

  handleAttendeeClick(attendee: any) {
    if (
      this.meetingDetails.participants.find(
        (p) => p.userPrincipalName == attendee.userPrincipalName,
      )
    ) {
      attendee.isAvailable = null;
      this.meetingDetails.participants =
        this.meetingDetails.participants.filter(
          (p) => p.userPrincipalName != attendee.userPrincipalName,
        );
    } else {
      this.meetingDetails.participants.push(attendee);
    }
  }

  async openAttendeesModal() {
    const modal = await this.modalController.create({
      component: AddAttendeeComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        initialAttendees: this.meetingDetails.participants,
        bookingType: 'book',
        startTime: this.meetingDetails.startTime,
        endTime: this.meetingDetails.endTime,
      },
    });
    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.meetingDetails.participants = result.data;
      }
    });
    modal.present();
  }

  checkIfCategoryExists(category: string) {
    if (category == 'Room') return true;
    if (category == 'Workspace') return true;
    return this.placesStore
      .uniquePlacesTagsAndAddresses()
      .tags.some((tag) => tag.toLowerCase() == category.toLowerCase());
  }

  onAttendeeModalDismiss() {
    this.attendeeAvailabilityLoading = true;
    const startTimeUTC = moment(this.meetingDetails.startTime)
      .utc()
      .format(DATETIMEFORMART);
    const endTimeUTC = moment(this.meetingDetails.endTime)
      .utc()
      .format(DATETIMEFORMART);
    const attendeeUPNList = this.meetingDetails.participants
      .map((participant) =>
        participant.isAvailable == null ? participant.userPrincipalName : null,
      )
      .filter((participant) => participant != null);
    if (attendeeUPNList.length == 0)
      return (this.attendeeAvailabilityLoading = false);
    this.apiService
      .checkAttendeeAvailability(attendeeUPNList, startTimeUTC, endTimeUTC)
      .subscribe((res) => {
        if (res) {
          this.meetingDetails.participants.forEach((attendee) => {
            if (res[attendee.userPrincipalName] != null)
              attendee.isAvailable = res[attendee.userPrincipalName];
          });
          this.attendeeAvailabilityLoading = false;
        }
      });
    this.attendeeSearchQuery = '';
  }

  startBookingProcess() {
    this.infoMessage = this.translateService.instant(
      'bookingStepper.bookingResult.bookingInProgress',
    );
    const meetingDetailsToBook = {
      ...this.meetingDetails,
      startTime: moment(this.meetingDetails.startTime)
        .utc()
        .format(DATETIMEFORMART),
      endTime: moment(this.meetingDetails.endTime)
        .utc()
        .format(DATETIMEFORMART),
    };

    this.webSocketService
      .createConnectionAndInvokeSearchPlacesForBookingStepper(
        meetingDetailsToBook,
      )
      .then(() => {
        this.bookingResultSubscription =
          this.webSocketService.bookingStepperResult$.subscribe((res) => {
            if (res.code == MeetingStepperErrorCodes.CapacityNotFound) {
              this.infoMessage = this.translateService.instant(
                'bookingStepper.bookingResult.capacityNotFound',
              );
            } else if (
              res.code == MeetingStepperErrorCodes.MicroSoftBookError
            ) {
              this.infoMessage = this.translateService.instant(
                'bookingStepper.bookingResult.microSoftBookError',
              );
              this.showTryAgainButton = true;
            } else if (res.code == MeetingStepperErrorCodes.EWSBookError) {
              this.infoMessage = this.translateService.instant(
                'bookingStepper.bookingResult.ewsBookError',
              );
              this.showTryAgainButton = true;
            } else if (res.code == MeetingStepperErrorCodes.ProccessEnd) {
              if (res.operationStatus == true) {
                // successfuly booked
                this.infoMessage = this.translateService.instant(
                  'bookingStepper.bookingResult.bookingSuccess',
                );
                this.calendarEvent = this.generateCalendarEvent(res.data);
              } else {
                if (res.data) {
                  // alternative resource found
                  this.infoMessage = this.translateService.instant(
                    'bookingStepper.bookingResult.alternativeBooking',
                  );
                  this.alternativeResource = res.data;
                } else {
                  // no alternative resource found and failed to book
                  this.infoMessage = this.translateService.instant(
                    'bookingStepper.bookingResult.bookingFailed',
                  );
                }
                this.showTryAgainButton = true;
              }
            }
          });
      });
  }

  bookAlternativeResource() {
    this.showTryAgainButton = false;
    const resource = this.alternativeResource;
    this.alternativeResource = null;
    this.infoMessage = this.translateService.instant(
      'bookingStepper.bookingResult.bookingAlternativeInProgress',
    );
    const eventRequest: EventRequest = {
      placeEmailAddress: resource.emailAddress,
      subject: this.meetingDetails.subject,
      startTime: moment(this.meetingDetails.startTime)
        .utc()
        .format(DATETIMEFORMART),
      endTime: moment(this.meetingDetails.endTime)
        .utc()
        .format(DATETIMEFORMART),
      reminderMinutesBeforeStart: this.meetingDetails.reminder,
      attendees: this.meetingDetails.participants,
      isOnPrem: resource.isOnPrem,
      // timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
      showAs: this.showAs,
      isOnlineMeeting: this.meetingDetails.isOnlineMeeting,
    };
    this.bookingsStore.makeNewBooking(eventRequest).subscribe({
      next: (res) => {
        this.calendarEvent = res;
        this.infoMessage = this.translateService.instant(
          'bookingStepper.bookingResult.bookingSuccess',
        );
      },
      error: (err) => {},
    });
  }

  filterAttendees(event: any) {
    const query = event.target.value.toLowerCase();
    this.filteredAttendees = this.invitableAttendees.filter(
      (attendee) =>
        attendee.firstName.toLowerCase().includes(query) ||
        attendee.lastName.toLowerCase().includes(query),
    );
  }

  generateCalendarEvent(place: any): CalendarEvent {
    const places = this.placesStore.places();
    const placeWithAvatar = places.find(
      (placeWithAvater) => placeWithAvater.emailAddress == place.emailAddress,
    );
    return {
      attendees: this.meetingDetails.participants,
      startTime: this.meetingDetails.startTime,
      endTime: this.meetingDetails.endTime,
      subject: this.meetingDetails.subject,
      isOrganizer: true,
      isOnlineMeeting: this.meetingDetails.isOnlineMeeting,
      place: placeWithAvatar ? placeWithAvatar : place,
      organizer: {
        name: this.userStore.user()?.displayName,
        emailAddress: this.userStore.user()?.userPrincipalName,
      },
      seriesMasterId: '',
      isNewEvent: true,
      editMasterEvent: false,
      recurrencePattern: null,
      onlineMeetingUrl: null,
      id: '',
      resourceStatus: 'None',
      checkedIn: false,
      allowNewTimeProposals: true,
    };
  }

  handleWorkingHoursToggle(event: any) {
    this.useWorkingHours = event.detail.checked;
    if (this.useWorkingHours) {
      const currentDate = moment(this.meetingDetails.startTime);
      this.meetingDetails.startTime = moment(currentDate)
        .set('hours', this.workingHours.startTime.hour)
        .set('minutes', this.workingHours.startTime.minute)
        .format(DATETIMEFORMART);
      this.meetingDetails.endTime = moment(currentDate)
        .set('hours', this.workingHours.endTime.hour)
        .set('minutes', this.workingHours.endTime.minute)
        .format(DATETIMEFORMART);
    } else {
      this.meetingDetails.startTime = moment()
        .add(5 - (moment().minute() % 5), 'minutes')
        .format(DATETIMEFORMART);
      this.meetingDetails.endTime = moment()
        .add(5 - (moment().minute() % 5) + 30, 'minutes')
        .format(DATETIMEFORMART);
    }
  }

  handleAcceptGreaterCapacity(event: any) {
    this.meetingDetails.acceptGreaterCapacity = event.detail.checked;
    if (this.meetingDetails.acceptGreaterCapacity) {
      this.meetingDetails.acceptSmallerCapacity = false;
    }
  }

  handleAcceptSmallerCapacity(event: any) {
    this.meetingDetails.acceptSmallerCapacity = event.detail.checked;

    if (this.meetingDetails.acceptSmallerCapacity) {
      this.meetingDetails.acceptGreaterCapacity = false;
    }
  }
}
